import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, resolveImg, formatApiError } from "../lib/api";
import { ALLERGENS, TAGS } from "../lib/menuConstants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PencilSimple, Trash, Plus, UploadSimple, ImageBroken, MagnifyingGlass, X } from "@phosphor-icons/react";
import { useTableSort } from "../lib/useTableSort";
import { SortHeader } from "../components/SortHeader";
export default function AdminMenu() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState("all");

  const load = async () => {
    try {
      const [c, m] = await Promise.all([api.get("/categories"), api.get("/menu-items")]);
      setCategories(c.data);
      setItems(m.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";
  const parentName = (id) => {
    const c = categories.find((x) => x.id === id);
    return c?.parent_id ? catName(c.parent_id) : null;
  };

  // Top-level categories (families) sorted by order
  const topLevels = useMemo(
    () => categories.filter((c) => !c.parent_id).sort((a, b) => a.order - b.order),
    [categories]
  );

  // For a family (top-level cat), collect its own id + child ids
  const familyIds = (topId) => {
    const kids = categories.filter((c) => c.parent_id === topId).map((k) => k.id);
    return [topId, ...kids];
  };

  const norm = (s) =>
    (s || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const filteredItems = useMemo(() => {
    const q = norm(search);
    const ids = familyFilter === "all" ? null : new Set(familyIds(familyFilter));
    return items.filter((it) => {
      if (ids && !ids.has(it.category_id)) return false;
      if (!q) return true;
      return norm(it.name).includes(q) || norm(it.description).includes(q);
    });
  }, [items, search, familyFilter, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  const { sortedRows: sortedItems, sortKey, sortDir, toggleSort } = useTableSort(filteredItems, "name", "asc");

  const toggleAvailable = async (item) => {
    // Optimistic update
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, available: !item.available } : i)));
    try {
      const { id, translations, ...rest } = item;  // eslint-disable-line no-unused-vars
      await api.put(`/menu-items/${item.id}`, { ...rest, available: !item.available });
    } catch (e) {
      toast.error(formatApiError(e));
      // Revert
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, available: item.available } : i)));
    }
  };

  const [translating, setTranslating] = useState(false);
  const regenerate = async () => {
    setTranslating(true);
    toast.info("Regenerando traducciones… puede tardar un minuto.");
    try {
      const r = await api.post("/admin/translate-all");
      toast.success(`Traducciones generadas (${r.data.items_translated} platos).`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="label-cap">carta digital</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Gestión de la carta</h1>
        </div>
        <Button
          variant="outline"
          className="border-[var(--border)]"
          onClick={regenerate}
          disabled={translating}
          data-testid="regenerate-translations-btn"
        >
          {translating ? "Traduciendo…" : "Regenerar traducciones (IA)"}
        </Button>
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items" data-testid="tab-menu-items">
            Platos ({items.length})
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-menu-categories">
            Categorías ({categories.length})
          </TabsTrigger>
        </TabsList>

        {/* ITEMS */}
        <TabsContent value="items" className="mt-6">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div className="flex flex-wrap items-end gap-3 flex-1">
              <div className="relative flex-1 min-w-[240px]">
                <label className="label-cap block mb-1.5">Buscar</label>
                <MagnifyingGlass size={16} className="absolute left-3 bottom-3 text-[var(--text-secondary)] pointer-events-none" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre o descripción…"
                  data-testid="menu-search"
                  className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm focus:outline-none focus:border-[var(--primary)]"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    data-testid="menu-search-clear"
                    className="absolute right-2 bottom-2 p-1 rounded hover:bg-[var(--bg)]"
                    aria-label="Limpiar"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <div className="min-w-[200px]">
                <label className="label-cap block mb-1.5">Familia</label>
                <select
                  value={familyFilter}
                  onChange={(e) => setFamilyFilter(e.target.value)}
                  data-testid="menu-family-filter"
                  className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm focus:outline-none focus:border-[var(--primary)]"
                >
                  <option value="all">Todas ({items.length})</option>
                  {topLevels.map((cat) => {
                    const n = items.filter((it) => familyIds(cat.id).includes(it.category_id)).length;
                    return (
                      <option key={cat.id} value={cat.id}>{cat.name} ({n})</option>
                    );
                  })}
                </select>
              </div>
              <div className="text-sm text-[var(--text-secondary)] pb-2.5" data-testid="menu-result-count">
                {filteredItems.length === items.length
                  ? `${items.length} platos`
                  : `${filteredItems.length} de ${items.length}`}
              </div>
            </div>
            <ItemDialog categories={categories} onSaved={load} />
          </div>
          <div className="card-flat overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Foto</TableHead>
                  <TableHead><SortHeader sortKey="name" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Nombre</SortHeader></TableHead>
                  <TableHead><SortHeader sortKey="category_id" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Categoría</SortHeader></TableHead>
                  <TableHead><SortHeader sortKey="price" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Precio</SortHeader></TableHead>
                  <TableHead>Etiquetas</TableHead>
                  <TableHead><SortHeader sortKey="available" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Visible</SortHeader></TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-[var(--text-secondary)]" data-testid="menu-empty">
                      {items.length === 0 ? "Sin platos todavía." : "Ningún plato coincide con los filtros."}
                    </TableCell>
                  </TableRow>
                ) : sortedItems.map((it) => (
                  <TableRow key={it.id} data-testid={`admin-menu-item-${it.id}`}>
                    <TableCell>
                      {it.image_url ? (
                        <img
                          src={resolveImg(it.image_url)}
                          alt=""
                          className="w-12 h-12 object-cover rounded-md"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-md bg-[var(--bg)] flex items-center justify-center text-[var(--text-secondary)]">
                          <ImageBroken size={16} />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{it.name}</TableCell>
                    <TableCell className="text-[var(--text-secondary)] text-sm">
                      {parentName(it.category_id) ? `${parentName(it.category_id)} › ` : ""}
                      {catName(it.category_id)}
                    </TableCell>
                    <TableCell>
                      {it.price_label === "Consultar"
                        ? "Consultar"
                        : `${it.price.toFixed(2)} €${it.price_label ? ` ${it.price_label}` : ""}`}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {it.tags?.map((tag) => (
                          <span
                            key={tag}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--primary)]/15 text-[var(--primary)]"
                          >
                            {TAGS.find((x) => x.id === tag)?.label || tag}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!it.available}
                        onCheckedChange={() => toggleAvailable(it)}
                        data-testid={`toggle-visible-${it.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <ItemDialog categories={categories} item={it} onSaved={load} />
                        <DeleteButton
                          testid={`delete-menu-item-${it.id}`}
                          onConfirm={async () => {
                            await api.delete(`/menu-items/${it.id}`);
                            toast.success("Plato eliminado");
                            load();
                          }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* CATEGORIES */}
        <TabsContent value="categories" className="mt-6">
          <div className="flex justify-end mb-4">
            <CategoryDialog categories={categories} onSaved={load} />
          </div>
          <CategoriesTable categories={categories} catName={catName} load={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CategoriesTable({ categories, catName, load }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useTableSort(categories, "name", "asc");
  return (
    <div className="card-flat overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead><SortHeader sortKey="name" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Nombre</SortHeader></TableHead>
            <TableHead><SortHeader sortKey="parent_id" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Familia</SortHeader></TableHead>
            <TableHead><SortHeader sortKey="kind" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Tipo</SortHeader></TableHead>
            <TableHead><SortHeader sortKey="order" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Orden</SortHeader></TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map((c) => (
            <TableRow key={c.id} data-testid={`admin-cat-${c.id}`}>
              <TableCell className="font-medium">
                {c.parent_id ? (
                  <span className="text-[var(--text-secondary)]">— </span>
                ) : null}
                {c.name}
              </TableCell>
              <TableCell className="text-[var(--text-secondary)]">
                {c.parent_id ? catName(c.parent_id) : "—"}
              </TableCell>
              <TableCell className="text-[var(--text-secondary)] capitalize">
                {c.kind}
              </TableCell>
              <TableCell>{c.order}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <CategoryDialog
                    categories={categories}
                    category={c}
                    onSaved={load}
                  />
                  <DeleteButton
                    testid={`delete-cat-${c.id}`}
                    onConfirm={async () => {
                      await api.delete(`/categories/${c.id}`);
                      toast.success("Categoría eliminada");
                      load();
                    }}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ImageUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/menu/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Imagen subida");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 rounded-md bg-[var(--bg)] overflow-hidden flex items-center justify-center shrink-0 border border-[var(--border)]">
        {value ? (
          <img
            src={resolveImg(value)}
            alt=""
            className="w-full h-full object-cover"
            data-testid="menu-item-image-preview"
          />
        ) : (
          <ImageBroken size={20} className="text-[var(--text-secondary)]" />
        )}
      </div>
      <div className="flex-1 space-y-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
          data-testid="menu-item-image-file"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="menu-item-upload-btn"
        >
          <UploadSimple size={14} className="mr-2" />
          {uploading ? "Subiendo…" : "Subir foto"}
        </Button>
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="text-xs"
          placeholder="o pega una URL"
          data-testid="menu-item-image-url"
        />
      </div>
    </div>
  );
}

function DeleteButton({ onConfirm, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-[var(--text-secondary)] hover:text-[var(--danger)] h-8 w-8"
          data-testid={testid}
        >
          <Trash size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>¿Eliminar?</DialogTitle>
        </DialogHeader>
        <p className="text-[var(--text-secondary)] text-sm">Esta acción no se puede deshacer.</p>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            className="btn-danger text-white"
            data-testid={`${testid}-confirm`}
            onClick={async () => {
              await onConfirm();
              setOpen(false);
            }}
          >
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-cap">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ItemDialog({ categories, item, onSaved }) {
  const editing = !!item;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    name: "", description: "", price: "", price_label: "",
    category_id: "", image_url: "", available: true,
    allergens: [], tags: [], variants: [], order: 0,
  });
  const [saving, setSaving] = useState(false);

  const leaves = useMemo(() => {
    const parents = new Set(categories.filter((c) => c.parent_id).map((c) => c.parent_id));
    return categories.filter((c) => !parents.has(c.id));
  }, [categories]);
  const catLabel = (c) =>
    c.parent_id
      ? `${categories.find((x) => x.id === c.parent_id)?.name} › ${c.name}`
      : c.name;

  useEffect(() => {
    if (open) {
      setF(
        editing
          ? {
              ...item,
              price: String(item.price),
              allergens: item.allergens || [],
              tags: item.tags || [],
              variants: (item.variants || []).map((v) => ({
                _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `v-${Math.random().toString(36).slice(2)}`,
                name: v.name,
                price: String(v.price),
              })),
            }
          : {
              name: "", description: "", price: "", price_label: "",
              category_id: leaves[0]?.id || "",
              image_url: "", available: true, allergens: [], tags: [], variants: [], order: 0,
            }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (field, id) =>
    setF((p) => ({
      ...p,
      [field]: p[field].includes(id) ? p[field].filter((x) => x !== id) : [...p[field], id],
    }));

  const save = async () => {
    const hasVariants = (f.variants || []).some((v) => v.name.trim());
    if (!f.name.trim() || !f.category_id || (f.price === "" && !hasVariants)) {
      toast.error("Completa nombre, categoría y precio (o tamaños)");
      return;
    }
    const payload = {
      ...f,
      price: parseFloat(f.price) || 0,
      order: parseInt(f.order) || 0,
      variants: (f.variants || [])
        .filter((v) => v.name.trim())
        .map((v) => ({ name: v.name.trim(), price: parseFloat(v.price) || 0 })),
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/menu-items/${item.id}`, payload);
      else await api.post("/menu-items", payload);
      toast.success(editing ? "Plato actualizado" : "Plato creado");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-[var(--text-secondary)] hover:text-[var(--primary)]"
            data-testid={`edit-menu-item-${item.id}`}
          >
            <PencilSimple size={16} />
          </Button>
        ) : (
          <Button className="btn-primary" data-testid="new-menu-item-btn">
            <Plus size={16} className="mr-2" />
            Nuevo plato
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="menu-item-dialog"
      >
        <DialogHeader>
          <DialogTitle>{editing ? "Editar plato" : "Nuevo plato"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Foto">
            <ImageUploader
              value={f.image_url}
              onChange={(url) => setF({ ...f, image_url: url })}
            />
          </Field>
          <Field label="Nombre">
            <Input
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              data-testid="menu-item-name"
            />
          </Field>
          <Field label="Descripción">
            <Textarea
              value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
              data-testid="menu-item-description"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Precio (€)">
              <Input
                type="number"
                step="0.01"
                value={f.price}
                onChange={(e) => setF({ ...f, price: e.target.value })}
                data-testid="menu-item-price"
              />
            </Field>
            <Field label="Sufijo precio">
              <Input
                value={f.price_label}
                onChange={(e) => setF({ ...f, price_label: e.target.value })}
                placeholder="/ud."
                data-testid="menu-item-price-label"
              />
            </Field>
            <Field label="Orden">
              <Input
                type="number"
                value={f.order}
                onChange={(e) => setF({ ...f, order: e.target.value })}
                data-testid="menu-item-order"
              />
            </Field>
          </div>
          <Field label="Categoría">
            <Select
              value={f.category_id}
              onValueChange={(v) => setF({ ...f, category_id: v })}
            >
              <SelectTrigger data-testid="menu-item-category">
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent>
                {leaves.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {catLabel(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tamaños / Variantes (opcional)">
            <div className="space-y-2">
              {f.variants.map((v, idx) => (
                <div
                  key={v._uid || `v-${idx}`}
                  className="flex items-center gap-2"
                  data-testid={`variant-row-${idx}`}
                >
                  <Input
                    value={v.name}
                    onChange={(e) =>
                      setF((p) => ({
                        ...p,
                        variants: p.variants.map((x, i) =>
                          i === idx ? { ...x, name: e.target.value } : x
                        ),
                      }))
                    }
                    placeholder="Ej. Xicoteta"
                    data-testid={`variant-name-${idx}`}
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={v.price}
                    onChange={(e) =>
                      setF((p) => ({
                        ...p,
                        variants: p.variants.map((x, i) =>
                          i === idx ? { ...x, price: e.target.value } : x
                        ),
                      }))
                    }
                    placeholder="€"
                    className="w-24"
                    data-testid={`variant-price-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-[var(--text-secondary)] hover:text-[var(--danger)] shrink-0"
                    onClick={() =>
                      setF((p) => ({
                        ...p,
                        variants: p.variants.filter((_, i) => i !== idx),
                      }))
                    }
                    data-testid={`variant-remove-${idx}`}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setF((p) => ({
                      ...p,
                      variants: [...p.variants, { _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `v-${Math.random().toString(36).slice(2)}`, name: "Xicoteta", price: "" }],
                    }))
                  }
                  data-testid="add-variant-xicoteta"
                >
                  + Xicoteta
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setF((p) => ({
                      ...p,
                      variants: [...p.variants, { _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `v-${Math.random().toString(36).slice(2)}`, name: "Gran", price: "" }],
                    }))
                  }
                  data-testid="add-variant-gran"
                >
                  + Gran
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setF((p) => ({
                      ...p,
                      variants: [...p.variants, { _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `v-${Math.random().toString(36).slice(2)}`, name: "", price: "" }],
                    }))
                  }
                  data-testid="add-variant"
                >
                  + Tamaño
                </Button>
              </div>
              <p className="text-[var(--text-secondary)] text-xs">
                Si añades tamaños, el precio se toma de cada tamaño. Útil para tapas
                (Xicoteta/Gran).
              </p>
            </div>
          </Field>
          <Field label="Etiquetas">
            <div className="flex flex-wrap gap-2">
              {TAGS.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggle("tags", tag.id)}
                  data-testid={`menu-tag-${tag.id}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    f.tags.includes(tag.id)
                      ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                      : "border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--primary)]"
                  }`}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Alérgenos">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ALLERGENS.map((a) => (
                <label
                  key={a.id}
                  className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer"
                  data-testid={`menu-allergen-${a.id}`}
                >
                  <Checkbox
                    checked={f.allergens.includes(a.id)}
                    onCheckedChange={() => toggle("allergens", a.id)}
                  />
                  <span className="text-base leading-none">{a.icon}</span>
                  {a.label}
                </label>
              ))}
            </div>
          </Field>
          <div className="flex items-center justify-between">
            <Label className="label-cap">Visible en la carta</Label>
            <Switch
              checked={f.available}
              onCheckedChange={(v) => setF({ ...f, available: v })}
              data-testid="menu-item-available"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            className="btn-primary"
            onClick={save}
            disabled={saving}
            data-testid="save-menu-item-btn"
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ categories, category, onSaved }) {
  const editing = !!category;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", kind: "comida", parent_id: "none", order: 0 });
  const topLevels = categories.filter((c) => !c.parent_id && c.id !== category?.id);

  useEffect(() => {
    if (open) {
      setF(
        editing
          ? {
              name: category.name,
              kind: category.kind,
              parent_id: category.parent_id || "none",
              order: category.order,
            }
          : { name: "", kind: "comida", parent_id: "none", order: 0 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    if (!f.name.trim()) {
      toast.error("Introduce un nombre");
      return;
    }
    const payload = {
      name: f.name,
      kind: f.kind,
      parent_id: f.parent_id === "none" ? null : f.parent_id,
      order: parseInt(f.order) || 0,
    };
    try {
      if (editing) await api.put(`/categories/${category.id}`, payload);
      else await api.post("/categories", payload);
      toast.success(editing ? "Categoría actualizada" : "Categoría creada");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-[var(--text-secondary)] hover:text-[var(--primary)]"
            data-testid={`edit-cat-${category.id}`}
          >
            <PencilSimple size={16} />
          </Button>
        ) : (
          <Button className="btn-primary" data-testid="new-cat-btn">
            <Plus size={16} className="mr-2" />
            Nueva categoría
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm" data-testid="category-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar categoría" : "Nueva categoría"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Nombre">
            <Input
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              data-testid="cat-name"
            />
          </Field>
          <Field label="Familia (opcional)">
            <Select
              value={f.parent_id}
              onValueChange={(v) => setF({ ...f, parent_id: v })}
            >
              <SelectTrigger data-testid="cat-parent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ninguna (principal)</SelectItem>
                {topLevels.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tipo">
            <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
              <SelectTrigger data-testid="cat-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="comida">Comida</SelectItem>
                <SelectItem value="bebida">Bebida</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Orden">
            <Input
              type="number"
              value={f.order}
              onChange={(e) => setF({ ...f, order: e.target.value })}
              data-testid="cat-order"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button className="btn-primary" onClick={save} data-testid="save-cat-btn">
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
